import { nanoid } from 'nanoid';
import { ReactNode } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { devtoolsDefaultOption } from '@/lib/zustand';

import { createTimer, Timer } from './timer';

export type InternalNotificationItem = {
  id: string;
  isOpen: boolean;
  timer: Timer;

  type: 'success' | 'info' | 'warning' | 'error';
  title: ReactNode;
  timestamp?: Date;
  description?: {
    body: ReactNode;
    url?: string;
  };
  duration?: number | 'infinite';
};

type InternalNotificationsStore = {
  max: number;
  notifications: {
    queued: InternalNotificationItem[];
    active: InternalNotificationItem[];
  };
  setMax: (max: number) => void;
  register: (
    notification: Omit<InternalNotificationItem, 'id' | 'isOpen' | 'timer'>,
    onDismiss: (id: string) => void,
  ) => void;
  unregister: (id: string) => string | undefined;
  setVisible: (id: string) => void;
  setInvisible: (id: string) => void;
};

// Internal implementations of the notification system
export const useInternalNotifications = create<InternalNotificationsStore>()(
  devtools(
    set => ({
      // Number of notifications that can be displayed on the screen at a time
      max: 5,

      notifications: {
        // Notifications waiting to be displayed
        queued: [],
        // Notifications currently displayed
        active: [],
      },

      setMax: max =>
        set(state => ({
          ...state,
          max: max,
        })),

      register: (notification, onDismiss) => {
        // Unique identifier for each notification
        const id = nanoid();

        // Duration of each notification
        const duration = notification.duration ?? 5000;

        set(state => {
          // When the notification expires, onDismiss is executed to remove it from the state.
          // (Since it takes 50 ms to switch a notification from hidden to visible, the minimum duration of each notification is 100 ms.)
          const timer = createTimer(
            () => {
              onDismiss(id);
            },
            duration === 'infinite'
              ? Number.POSITIVE_INFINITY
              : duration > 100
                ? duration
                : 100,
          );

          const queuedList = [...state.notifications.queued];
          const activeList = [...state.notifications.active];
          const activeNum = activeList.length;

          // If there are too many notifications displayed, ...
          if (activeNum >= state.max) {
            // ... the new notification is added to the queue.
            queuedList.push({
              id: id,
              isOpen: false,
              timer: timer,
              ...notification,
            });
          } else {
            // Otherwise, show it immediately.
            activeList.push({
              id: id,
              isOpen: false,
              timer: timer,
              ...notification,
            });

            // Starts the timer for the notification.
            timer.resume();

            // Since it is in a hidden state initially, switch to the visible state after 50 milliseconds.
            // (`opacity-0` -> `opacity-100`)
            createTimer(() => state.setVisible(id), 50).resume();
          }

          // Reflect updated state
          return {
            ...state,
            notifications: {
              queued: queuedList,
              active: activeList,
            },
          };
        });
      },

      unregister: id => {
        // If there is a notification to be displayed next, its ID is returned to the caller.
        let nextId: string | undefined = undefined;

        set(state => {
          // Remove the target notification from the currently displayed notifications.
          const remainingActiveList = state.notifications.active.filter(
            n => n.id !== id,
          );
          const activeNum = remainingActiveList.length;

          if (activeNum < state.max) {
            // Get the next notification to be displayed from the queue.
            const nextNotification = state.notifications.queued.shift();

            // If the next notification exists, ...
            if (nextNotification) {
              // ...add it to the currently displayed notification list, ...
              remainingActiveList.push(nextNotification);
              // ...start its timer, ...
              nextNotification.timer.resume();
              // ...and get its id.
              nextId = nextNotification.id;
            }
          }

          // Reflect updated state
          return {
            ...state,
            notifications: {
              queued: [...state.notifications.queued],
              active: [...remainingActiveList],
            },
          };
        });

        return nextId;
      },

      setVisible: id =>
        set(state => ({
          ...state,
          notifications: {
            queued: [...state.notifications.queued],
            active: state.notifications.active.map(n =>
              n.id === id ? { ...n, isOpen: true } : n,
            ),
          },
        })),

      setInvisible: id =>
        set(state => ({
          ...state,
          notifications: {
            queued: [...state.notifications.queued],
            active: state.notifications.active.map(n =>
              n.id === id ? { ...n, isOpen: false } : n,
            ),
          },
        })),
    }),
    { ...devtoolsDefaultOption, name: 'useInternalNotifications' },
  ),
);
